
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface CustomerNote {
  id: string;
  provider_id: string;
  customer_email: string;
  note_content: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export const useCustomerNotes = (providerId: string, customerEmail: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: notes, isLoading, error } = useQuery({
    queryKey: ['customer-notes', providerId, customerEmail],
    queryFn: async () => {
      if (!providerId || !customerEmail) return [];
      
      const { data, error } = await supabase
        .from('customer_notes')
        .select('*')
        .eq('provider_id', providerId)
        .eq('customer_email', customerEmail)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching customer notes:', error);
        throw error;
      }

      return data as CustomerNote[];
    },
    enabled: !!providerId && !!customerEmail,
  });

  const addNoteMutation = useMutation({
    mutationFn: async ({ noteContent }: { noteContent: string }) => {
      const { data, error } = await supabase
        .from('customer_notes')
        .insert({
          provider_id: providerId,
          customer_email: customerEmail,
          note_content: noteContent,
          created_by: (await supabase.auth.getUser()).data.user?.id || '',
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-notes', providerId, customerEmail] });
      toast({
        title: "Note Added",
        description: "Customer note has been added successfully.",
      });
    },
    onError: (error) => {
      console.error('Error adding customer note:', error);
      toast({
        title: "Error",
        description: "Failed to add customer note.",
        variant: "destructive",
      });
    },
  });

  const deleteNoteMutation = useMutation({
    mutationFn: async (noteId: string) => {
      const { error } = await supabase
        .from('customer_notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-notes', providerId, customerEmail] });
      toast({
        title: "Note Deleted",
        description: "Customer note has been deleted successfully.",
      });
    },
    onError: (error) => {
      console.error('Error deleting customer note:', error);
      toast({
        title: "Error",
        description: "Failed to delete customer note.",
        variant: "destructive",
      });
    },
  });

  return {
    notes,
    isLoading,
    error,
    addNote: addNoteMutation.mutate,
    deleteNote: deleteNoteMutation.mutate,
    isAddingNote: addNoteMutation.isPending,
    isDeletingNote: deleteNoteMutation.isPending,
  };
};
